import { safeSessionStorage } from '@/lib/safeStorage'
import {
  CHUNK_HEAL_PARAM,
  CHUNK_RELOAD_STORAGE_KEY,
  type ChunkRecoveryReason,
  clearChunkHealMarkerFromHref,
  computeChunkReloadDecision,
  isRecoverableChunkError,
  isStaleAssetUrl,
  matchRecoverableChunkError,
} from './chunkReloadRecoveryCore'

const LAST_ERROR_KEY = 'taisen-board:last-client-error'

let installed = false
let lastPageShowPersisted: boolean | undefined

type ChunkRecoveryEntry = {
  at: string
  type: 'chunk-recovery'
  message: string
  path: string
  visibilityState: DocumentVisibilityState
  persisted?: boolean
  reason: ChunkRecoveryReason
  detail?: string
}

function currentPath(): string {
  return window.location.pathname + window.location.search
}

function saveChunkRecoveryBreadcrumb(reason: ChunkRecoveryReason, detail?: string) {
  try {
    const entry: ChunkRecoveryEntry = {
      at: new Date().toISOString(),
      type: 'chunk-recovery',
      message: 'Stale application asset detected; attempting recovery.',
      path: currentPath(),
      visibilityState: document.visibilityState,
      persisted: lastPageShowPersisted,
      reason,
      detail,
    }
    safeSessionStorage.setJSON(LAST_ERROR_KEY, entry)
  } catch {
    // Breadcrumbs must never block recovery.
  }
}

function getPreviousReloadAt(): { value: number | null; storageAvailable: boolean } {
  try {
    const raw = safeSessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY)
    if (raw === null) return { value: null, storageAvailable: true }
    const parsed = Number(raw)
    return { value: Number.isFinite(parsed) ? parsed : null, storageAvailable: true }
  } catch {
    return { value: null, storageAvailable: false }
  }
}

function describeValue(value: unknown): string {
  if (value instanceof Error) return value.message
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function eventResourceUrl(event: Event): string | null {
  const target = event.target
  if (target instanceof HTMLScriptElement) return target.src
  if (target instanceof HTMLLinkElement) return target.href
  return null
}

function isRecoverableResourceError(event: Event): { matched: boolean; detail?: string } {
  const resourceUrl = eventResourceUrl(event)
  if (!resourceUrl) return { matched: false }
  if (!isStaleAssetUrl(resourceUrl, window.location.origin)) return { matched: false }
  return { matched: true, detail: resourceUrl }
}

function extractPreloadDetail(event: Event): string {
  const custom = event as Event & { payload?: unknown }
  if (custom.payload !== undefined) return describeValue(custom.payload)
  return event.type
}

export { isRecoverableChunkError }
export type { ChunkRecoveryReason }

export function recoverFromChunkLoadFailure(reason: ChunkRecoveryReason, detail?: string): boolean {
  if (typeof window === 'undefined') return false

  saveChunkRecoveryBreadcrumb(reason, detail)

  const now = Date.now()
  const previous = getPreviousReloadAt()
  const decision = computeChunkReloadDecision({
    href: window.location.href,
    now,
    previousReloadAt: previous.value,
    storageAvailable: previous.storageAvailable,
  })

  if (!decision.shouldReload || !decision.nextHref) return false

  const wrote = safeSessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now))
  if (!wrote) {
    // URL marker is the primary loop guard, so storage failure does not block recovery.
  }
  window.location.replace(decision.nextHref)
  return true
}

export function clearChunkHealMarkerAfterHealthyRender(delayMs = 1_000) {
  if (typeof window === 'undefined') return
  if (!window.location.search.includes(CHUNK_HEAL_PARAM)) return

  window.setTimeout(() => {
    try {
      const nextHref = clearChunkHealMarkerFromHref(window.location.href)
      if (nextHref !== window.location.href) {
        window.history.replaceState(window.history.state, '', nextHref)
      }
    } catch {
      // Keeping the marker is safer than throwing during recovery.
    }
  }, delayMs)
}

export function installChunkReloadRecoveryHandlers() {
  if (typeof window === 'undefined' || installed) return
  installed = true

  window.addEventListener('pageshow', (event) => {
    lastPageShowPersisted = event.persisted
  })

  window.addEventListener('vite:preloadError', (event) => {
    const detail = extractPreloadDetail(event)
    if (recoverFromChunkLoadFailure('vite-preload-error', detail)) {
      event.preventDefault()
    }
  })

  window.addEventListener('unhandledrejection', (event) => {
    const match = matchRecoverableChunkError(event.reason, window.location.origin)
    if (!match.matched) return
    if (recoverFromChunkLoadFailure('unhandledrejection', match.detail)) {
      event.preventDefault()
    }
  })

  window.addEventListener(
    'error',
    (event) => {
      const resource = isRecoverableResourceError(event)
      if (resource.matched) {
        recoverFromChunkLoadFailure('resource-error', resource.detail)
        return
      }

      if (event instanceof ErrorEvent) {
        const match = matchRecoverableChunkError(event.error ?? event.message, window.location.origin)
        if (match.matched) recoverFromChunkLoadFailure('resource-error', match.detail)
      }
    },
    true,
  )
}
